import { useCallback, useEffect, useRef, useState } from 'react'
import { toApiError } from 'utility/crm/api-error'
import { subscribeCrm } from './events'

/** @typedef {import('utility/crm/types').CrmApiError} CrmApiError */

/**
 * Fetch-on-mount hook in the repo's { data, loading, error } style.
 * - refetches when `deps` change or when a subscribed scope is invalidated
 * - keeps the previous data while refetching (no flash of empty)
 * - ignores out-of-order responses
 *
 * @template T
 * @param {() => Promise<T>} fetcher
 * @param {unknown[]} deps values that should trigger a refetch
 * @param {{ scopes?: import('./events').CrmScope[], enabled?: boolean }} [options]
 * @returns {{ data: T|null, loading: boolean, error: CrmApiError|null, refetch: () => void }}
 */
export const useCrmQuery = (fetcher, deps, { scopes = [], enabled = true } = {}) => {
    const [state, setState] = useState({ data: null, loading: enabled, error: null })
    const [tick, setTick] = useState(0)
    const requestId = useRef(0)
    const fetcherRef = useRef(fetcher)
    fetcherRef.current = fetcher
    const scopeKey = scopes.join(',')

    useEffect(
        () =>
            subscribeCrm(changed => {
                if (changed.some(scope => scopeKey.split(',').includes(scope))) setTick(t => t + 1)
            }),
        [scopeKey],
    )

    useEffect(() => {
        if (!enabled) {
            setState({ data: null, loading: false, error: null })
            return undefined
        }
        requestId.current += 1
        const id = requestId.current
        setState(prev => ({ ...prev, loading: true, error: null }))
        fetcherRef
            .current()
            .then(data => {
                if (id === requestId.current) setState({ data, loading: false, error: null })
            })
            .catch(err => {
                if (id === requestId.current) {
                    setState(prev => ({ data: prev.data, loading: false, error: toApiError(err) }))
                }
            })
        return () => {
            // A newer request (or unmount) supersedes this one.
            requestId.current += 1
        }
    }, [...deps, tick, enabled])

    const refetch = useCallback(() => setTick(t => t + 1), [])

    return { ...state, refetch }
}
